import { CalendarClock, Pause, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { MOCK_SCHEDULES, type ScreeningSchedule } from '@/data/aml/mockData';
import { cn } from '@/lib/utils';

function statusBadgeClass(status: ScreeningSchedule['status']): string {
	if (status === 'active') return 'bg-green-50 text-green-700 border-green-200';
	if (status === 'paused')
		return 'bg-yellow-50 text-yellow-700 border-yellow-200';
	return 'bg-red-50 text-red-700 border-red-200';
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

export default function ScreeningSchedulePage() {
	const [schedules, setSchedules] =
		useState<ScreeningSchedule[]>(MOCK_SCHEDULES);
	const [addOpen, setAddOpen] = useState(false);
	const [newName, setNewName] = useState('');
	const [newType, setNewType] = useState<'batch' | 'continuous'>('batch');
	const [newFrequency, setNewFrequency] = useState('');

	const toggle = (id: string) => {
		setSchedules((prev) =>
			prev.map((s) => {
				if (s.id !== id) return s;
				const next: ScreeningSchedule['status'] =
					s.status === 'active' ? 'paused' : 'active';
				toast.success(`Schedule ${next === 'active' ? 'resumed' : 'paused'}`);
				return { ...s, status: next };
			}),
		);
	};

	const remove = (id: string) => {
		setSchedules((prev) => prev.filter((s) => s.id !== id));
		toast.success('Schedule deleted');
	};

	const handleAdd = () => {
		if (!newName.trim() || !newFrequency) {
			toast.error('Fill in all required fields');
			return;
		}
		setSchedules((prev) => [
			{
				id: `schedule_${Date.now()}`,
				name: newName,
				type: newType,
				frequency: newFrequency,
				last_run: new Date(Date.now() - 3600000).toISOString(),
				next_run: new Date(Date.now() + 3600000).toISOString(),
				status: 'active',
				records_count: 0,
			},
			...prev,
		]);
		setAddOpen(false);
		setNewName('');
		setNewType('batch');
		setNewFrequency('');
		toast.success('Schedule created');
	};

	return (
		<div className="min-h-screen bg-background">
			<Toaster richColors position="top-right" />
			<div className="border-b border-border bg-white px-6 py-4">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-xl font-semibold text-[#0A0A0A]">
							Screening Schedule
						</h1>
						<p className="text-sm text-[#737373] mt-0.5">
							Manage automated screening jobs and schedules
						</p>
					</div>
					<Button
						className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90 gap-2"
						onClick={() => setAddOpen(true)}
					>
						<Plus className="h-4 w-4" /> New Schedule
					</Button>
				</div>
			</div>

			<div className="p-6 space-y-3 max-w-[900px]">
				{schedules.length === 0 ? (
					<div className="rounded-xl border border-dashed border-[#D4D4D4] p-12 text-center text-sm text-[#737373]">
						No schedules configured.
					</div>
				) : (
					schedules.map((s) => (
						<Card key={s.id} className="border border-[#E5E5E5]">
							<CardContent className="p-4">
								<div className="flex flex-wrap items-start justify-between gap-4">
									<div className="flex items-start gap-3">
										<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#F5F5F5]">
											<CalendarClock className="h-5 w-5 text-[#737373]" />
										</div>
										<div>
											<div className="text-sm font-semibold text-[#0A0A0A]">
												{s.name}
											</div>
											<div className="flex flex-wrap items-center gap-2 mt-1">
												<span
													className={cn(
														'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
														statusBadgeClass(s.status),
													)}
												>
													{s.status.charAt(0).toUpperCase() + s.status.slice(1)}
												</span>
												<Badge variant="outline" className="text-xs">
													{s.type === 'batch' ? 'Batch' : 'Continuous'}
												</Badge>
												<span className="text-xs text-[#737373]">
													{s.frequency}
												</span>
											</div>
										</div>
									</div>

									<div className="flex items-center gap-2">
										<div className="hidden sm:flex flex-col items-end gap-0.5 mr-4">
											<span className="text-xs text-[#737373]">
												Last run: {formatDate(s.last_run)}
											</span>
											<span className="text-xs text-[#737373]">
												Next run: {formatDate(s.next_run)}
											</span>
											<span className="text-xs text-[#A3A3A3]">
												{s.records_count.toLocaleString()} records
											</span>
										</div>
										<Button
											variant="outline"
											size="sm"
											className="gap-1"
											onClick={() => toggle(s.id)}
										>
											{s.status === 'active' ? (
												<>
													<Pause className="h-3.5 w-3.5" /> Pause
												</>
											) : (
												<>
													<Play className="h-3.5 w-3.5" /> Resume
												</>
											)}
										</Button>
										<Button
											variant="outline"
											size="sm"
											className="gap-1"
											onClick={() => toast.info('Edit schedule')}
										>
											<Pencil className="h-3.5 w-3.5" /> Edit
										</Button>
										<Button
											variant="ghost"
											size="sm"
											className="text-red-600 hover:text-red-700 gap-1"
											onClick={() => remove(s.id)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									</div>
								</div>
							</CardContent>
						</Card>
					))
				)}
			</div>

			<Dialog open={addOpen} onOpenChange={setAddOpen}>
				<DialogContent className="max-w-[480px]">
					<DialogHeader>
						<DialogTitle>New Screening Schedule</DialogTitle>
						<DialogDescription>
							Configure a new automated screening job.
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="space-y-1.5">
							<Label>
								Schedule Name <span className="text-red-500">*</span>
							</Label>
							<Input
								value={newName}
								onChange={(e) => setNewName(e.target.value)}
								placeholder="e.g. Daily Customer Screening"
							/>
						</div>
						<div className="space-y-1.5">
							<Label>Type</Label>
							<Select
								value={newType}
								onValueChange={(v) => setNewType(v as 'batch' | 'continuous')}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="batch">Batch</SelectItem>
									<SelectItem value="continuous">Continuous</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label>
								Frequency <span className="text-red-500">*</span>
							</Label>
							<Select value={newFrequency} onValueChange={setNewFrequency}>
								<SelectTrigger>
									<SelectValue placeholder="Select frequency" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="Hourly">Hourly</SelectItem>
									<SelectItem value="Every 6 hours">Every 6 hours</SelectItem>
									<SelectItem value="Daily">Daily</SelectItem>
									<SelectItem value="Weekly">Weekly</SelectItem>
									<SelectItem value="Monthly">Monthly</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setAddOpen(false)}>
							Cancel
						</Button>
						<Button
							className="bg-[#0A0A0A] text-white hover:bg-[#0A0A0A]/90"
							onClick={handleAdd}
						>
							Create Schedule
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
